const SESSION_STORAGE_KEY = 'fans.audio.eventSessionId';
const VISITOR_ID_KEYS = ['fans.audio.visitorId', 'visitorId'];
const PRIMARY_VISITOR_ID_KEY = VISITOR_ID_KEYS[0];

const ID_REGEX = /^[A-Za-z0-9_-]{8,128}$/;

function isValidId(value: string | null | undefined): value is string {
  return typeof value === 'string' && ID_REGEX.test(value);
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '_');
  }
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function getOrCreateEventSessionId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (isValidId(existing)) return existing;

    const created = randomId();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return undefined;
  }
}

export function getExistingVisitorId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    for (const key of VISITOR_ID_KEYS) {
      const fromSession = window.sessionStorage.getItem(key);
      if (isValidId(fromSession)) return fromSession;

      const fromLocal = window.localStorage.getItem(key);
      if (isValidId(fromLocal)) return fromLocal;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function getOrCreatePersistentVisitorId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const existing = getExistingVisitorId();
  if (existing) return existing;

  const created = randomId();

  try {
    window.localStorage.setItem(PRIMARY_VISITOR_ID_KEY, created);
  } catch {
    try {
      window.sessionStorage.setItem(PRIMARY_VISITOR_ID_KEY, created);
    } catch {
      return undefined;
    }
  }

  return created;
}

export function getShareIdFromSearch(search: string): string | undefined {
  const params = new URLSearchParams(search);
  const raw = params.get('shareId');
  if (!isValidId(raw)) return undefined;
  return raw;
}

export function getEventCorrelation(search: string, includeShareId: boolean): {
  sessionId?: string;
  visitorId?: string;
  shareId?: string;
} {
  const sessionId = getOrCreateEventSessionId();
  const visitorId = getExistingVisitorId();
  const shareId = includeShareId ? getShareIdFromSearch(search) : undefined;

  return {
    ...(sessionId ? { sessionId } : {}),
    ...(visitorId ? { visitorId } : {}),
    ...(shareId ? { shareId } : {}),
  };
}
